// ================================================================
// 美化分享卡片 Modal
// 使用 html2canvas 截圖並下載為 PNG
// ================================================================

import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';

interface ShareCardModalProps {
  open: boolean;
  onClose: () => void;
  questionText: string;
  summary: string;
  category?: string;
  queryDate?: string;
  score?: number;        // 配對分數 0-100（可選）
  grade?: string;        // 配對等級文字（可選，如「非常契合」）
}

// ----------------------------------------------------------------
// 從完整報告中提取有意義的摘要
// 策略：優先找結論/總結段落，其次找含評分的段落，最後取中後段
// ----------------------------------------------------------------
export const extractSummary = (content: string, maxChars = 320): string => {
  if (!content) return '';

  const lines = content.split('\n').map(l => l.trim());

  // 清洗單行：移除格式標記，保留純文字
  const cleanLine = (l: string): string =>
    l.replace(/\[OK\]|\[ERROR\]|\[WARN\]/g, '')
     .replace(/#+\s*/g, '')
     .replace(/\*+/g, '')
     .replace(/◆|■|▶|【|】/g, '')
     .replace(/^\s*[-*·\d]+[.)]\s*/, '')
     .trim();

  // 判斷該行是否需要跳過
  const skipLine = (l: string): boolean => {
    if (!l || l.length < 3) return true;
    if (l.startsWith('|') || l.startsWith('---')) return true;
    if (l.match(/^\d+月\s*\|/)) return true;   // ASCII 圖表
    return false;
  };

  // 收集乾淨文字，在完整句子邊界截斷
  const collect = (startIdx: number, count: number): string => {
    let buf = '';
    for (let i = startIdx; i < startIdx + count && i < lines.length; i++) {
      if (skipLine(lines[i])) continue;
      const clean = cleanLine(lines[i]);
      if (clean.length < 3) continue;
      buf += (buf ? '' : '') + clean;
    }
    if (buf.length <= maxChars) return buf;
    // 在 maxChars 前找最後一個完整句號截斷
    const sentenceEnd = /[。！？\.!?]/g;
    let lastEnd = -1;
    let m: RegExpExecArray | null;
    sentenceEnd.lastIndex = 0;
    while ((m = sentenceEnd.exec(buf)) !== null) {
      if (m.index + 1 <= maxChars) lastEnd = m.index + 1;
      else break;
    }
    return lastEnd > 30 ? buf.substring(0, lastEnd) : buf.substring(0, maxChars) + '…';
  };

  // 結論關鍵字（出現在這些行之後的內容最有價值）
  const conclusionKeywords = [
    '總結', '結論', '綜合評估', '整體建議', '最終建議',
    '配對結論', '分析結論', '綜合分析', '整體評價', '小結',
  ];

  // Step 1：找結論段落起始行
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (conclusionKeywords.some(kw => l.includes(kw)) && l.length < 25) {
      const text = collect(i + 1, 25);
      if (text.length > 40) return text;
    }
  }

  // Step 2：跳過前 25% 行（開場白），從中後段取文字
  const skip = Math.floor(lines.length * 0.25);
  const midText = collect(skip, 30);
  if (midText.length > 40) return midText;

  // Step 3：兜底
  return collect(0, lines.length) || content.substring(0, maxChars) + '…';
};

// ----------------------------------------------------------------
// 分數轉顏色
// ----------------------------------------------------------------
const scoreColor = (s: number) => {
  if (s >= 80) return '#38ef7d';
  if (s >= 65) return '#f7971e';
  return '#f953c6';
};

// ----------------------------------------------------------------
// 主元件
// ----------------------------------------------------------------
const ShareCardModal: React.FC<ShareCardModalProps> = ({
  open,
  onClose,
  questionText,
  summary,
  category,
  queryDate,
  score,
  grade,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const link = document.createElement('a');
      link.download = `howyun-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('截圖失敗', err);
    } finally {
      setDownloading(false);
    }
  };

  const today = queryDate || new Date().toLocaleDateString('zh-TW', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>分享卡片預覽</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          預覽下方卡片，點擊「下載圖片」儲存後即可分享
        </Typography>

        {/* ---- 分享卡片本體（被 html2canvas 截圖的目標） ---- */}
        <Box
          ref={cardRef}
          sx={{
            background: 'linear-gradient(135deg, #1a0533 0%, #0d3b36 100%)',
            borderRadius: 3,
            p: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* 裝飾圓形 */}
          <Box sx={{
            position: 'absolute', top: -40, right: -40,
            width: 150, height: 150, borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -30, left: -30,
            width: 100, height: 100, borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          }} />

          {/* Logo + 類別 */}
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: '#ffd700' }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ letterSpacing: 1, flexGrow: 1 }}>
              我的好運氣
            </Typography>
            {category && (
              <Box sx={{
                px: 1, py: 0.25, borderRadius: 1,
                background: 'rgba(255,255,255,0.15)',
                fontSize: '0.7rem', fontFamily: 'sans-serif',
              }}>
                {category}
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 2 }} />

          {/* 問題標題 */}
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: 2, lineHeight: 1.5, fontSize: '0.95rem' }}
          >
            {questionText}
          </Typography>

          {/* 配對分數區（score 存在時顯示） */}
          {score !== undefined && (
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 2,
                p: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  border: `3px solid ${scoreColor(score)}`,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 'bold', lineHeight: 1, color: scoreColor(score) }}>
                  {score}
                </Typography>
                <Typography sx={{ fontSize: '0.55rem', opacity: 0.8 }}>分</Typography>
              </Box>
              <Box>
                {grade && (
                  <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: scoreColor(score) }}>
                    {grade}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.75rem', opacity: 0.7, mt: 0.25 }}>
                  紫微斗數命盤配對指數
                </Typography>
              </Box>
            </Box>
          )}

          {/* 摘要文字 */}
          {summary && (
            <Box sx={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8, fontSize: '0.82rem', opacity: 0.9 }}>
                {summary}
              </Typography>
            </Box>
          )}

          {/* 底部 */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem' }}>
              {today}
            </Typography>
            <Typography variant="caption" sx={{
              opacity: 0.85,
              background: 'rgba(255,215,0,0.2)',
              px: 1, py: 0.25, borderRadius: 1,
              fontSize: '0.65rem',
            }}>
              紫微斗數分析 · howyun.me
            </Typography>
          </Box>
        </Box>
        {/* ---- 卡片結束 ---- */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">關閉</Button>
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading}
          sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
        >
          下載圖片
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareCardModal;
